import React, { useState, useRef, useEffect } from 'react';
import { mcpClient } from '../services/mcpClient';
import { chatGPTService } from '../services/chatGPTService';
import ErrorMessage from './ErrorMessage';

interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  error?: string;
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content: '👋 Hello! I\'m your AI financial assistant powered by real-time market data. I can help you with stocks, market trends, financial news, and currency rates. What would you like to know about today\'s markets?',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatGPTAvailable, setChatGPTAvailable] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check ChatGPT availability on component mount
  useEffect(() => {
    const checkChatGPTAvailability = () => {
      const isAvailable = chatGPTService.isAvailable();
      setChatGPTAvailable(isAvailable);
      if (isAvailable) {
        console.log('✅ ChatGPT integration is available');
      } else {
        console.log('⚠️ ChatGPT API key not configured - using fallback responses');
      }
    };
    
    checkChatGPTAvailability();
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      // First, try to get financial data if it's a financial query
      const financialResponse = await parseAndExecuteQuery(inputMessage.trim());
      
      let finalResponse = financialResponse;
      
      // If ChatGPT is available and this seems like a general conversation or needs enhancement
      if (chatGPTAvailable && shouldUseChatGPT(inputMessage.trim())) {
        try {
          // Enhance the response with ChatGPT
          const chatGPTResponse = await chatGPTService.generateFinancialResponse(
            inputMessage.trim(),
            financialResponse
          );
          finalResponse = chatGPTResponse;
        } catch (chatGPTError) {
          console.warn('ChatGPT enhancement failed, using fallback:', chatGPTError);
          // Use the original financial response if ChatGPT fails
        }
      }
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: finalResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Query error:', err);
      
      // Try ChatGPT for general conversation if available
      if (chatGPTAvailable && isGeneralConversation(inputMessage.trim())) {
        try {
          const chatGPTResponse = await chatGPTService.generateGeneralResponse(inputMessage.trim());
          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            content: chatGPTResponse,
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
        } catch (chatGPTError) {
          console.warn('ChatGPT response failed:', chatGPTError);
          // Fall back to default response
          showDefaultResponse(err);
        }
      } else {
        showDefaultResponse(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showDefaultResponse = (err: any) => {
    const errorMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      content: 'I\'m a financial assistant that can help you with:\n\n• **Stock Quotes**: "Get quote for AAPL" or "Price of TSLA"\n• **Trending Stocks**: "Show trending stocks" or "What\'s trending?"\n• **Market Indices**: "Get market indices" or "S&P 500"\n• **Financial News**: "Get financial news" or "News about Tesla"\n• **Currency Rates**: "USD to EUR rate" or "Exchange rate USD/EUR"\n\nTry one of these queries!',
      isUser: false,
      timestamp: new Date(),
      error: err instanceof Error ? err.message : 'Unknown error'
    };
    setMessages(prev => [...prev, errorMessage]);
  };

  const shouldUseChatGPT = (query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    // Use ChatGPT for general conversation, questions, or when user asks for explanations
    return (
      lowerQuery.includes('what') ||
      lowerQuery.includes('how') ||
      lowerQuery.includes('why') ||
      lowerQuery.includes('explain') ||
      lowerQuery.includes('tell me') ||
      lowerQuery.includes('?') ||
      isGeneralConversation(query)
    );
  };

  const isGeneralConversation = (query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    return (
      lowerQuery.includes('hello') ||
      lowerQuery.includes('hi') ||
      lowerQuery.includes('hey') ||
      lowerQuery.includes('thank') ||
      lowerQuery.includes('goodbye') ||
      lowerQuery.includes('bye') ||
      lowerQuery.includes('help') ||
      lowerQuery.includes('what can you do') ||
      (!lowerQuery.includes('quote') && 
       !lowerQuery.includes('price') && 
       !lowerQuery.includes('stock') &&
       !lowerQuery.includes('trending') &&
       !lowerQuery.includes('market') &&
       !lowerQuery.includes('news') &&
       !lowerQuery.includes('currency') &&
       !lowerQuery.includes('rate'))
    );
  };

  const parseAndExecuteQuery = async (query: string): Promise<string> => {
    const lowerQuery = query.toLowerCase();
    
    // Greeting responses
    if (lowerQuery.includes('hello') || lowerQuery.includes('hi') || lowerQuery.includes('hey')) {
      return "👋 Hello! I'm your financial assistant powered by real-time market data. I can help you with:\n\n• Stock quotes and prices\n• Trending stocks\n• Market indices\n• Financial news\n• Currency exchange rates\n\nWhat would you like to know about today's markets?";
    }
    
    // Help requests
    if (lowerQuery.includes('help') || lowerQuery.includes('what can you do')) {
      return "🤖 I'm a financial AI assistant that can help you with market data and analysis. Here's what I can do:\n\n📈 **Get Stock Information**:\n• \"What's the price of AAPL?\"\n• \"Show me Tesla stock quote\"\n• \"Get quote for MSFT\"\n\n🔥 **Market Trends**:\n• \"What stocks are trending?\"\n• \"Show me popular stocks\"\n• \"What's hot in the market?\"\n\n📊 **Market Overview**:\n• \"How are the markets doing?\"\n• \"Show me market indices\"\n• \"S&P 500 performance\"\n\n📰 **Financial News**:\n• \"Get latest financial news\"\n• \"News about Apple\"\n• \"Market news today\"\n\n💱 **Currency Rates**:\n• \"USD to EUR rate\"\n• \"Exchange rate USD/GBP\"\n• \"Currency conversion\"\n\nJust ask me anything about finance and markets!";
    }
    
    // Stock quotes - enhanced detection
    if (lowerQuery.includes('quote') || lowerQuery.includes('price') || lowerQuery.includes('stock') || 
        lowerQuery.includes('how much') || lowerQuery.includes('cost') || lowerQuery.includes('worth')) {
      const symbolMatch = query.match(/\b([A-Z]{1,5})\b/);
      if (symbolMatch) {
        const symbol = symbolMatch[1];
        const response = await mcpClient.getStockQuotes(symbol);
        if (response.success && response.data.length > 0) {
          const quote = response.data[0];
          const changeColor = quote.change && quote.change >= 0 ? '📈' : '📉';
          const changeText = quote.change && quote.changePercent ? 
            `${quote.change >= 0 ? '+' : ''}${quote.change.toFixed(2)} (${quote.change >= 0 ? '+' : ''}${quote.changePercent.toFixed(2)}%)` : 
            'N/A';
          
          return `📈 **${quote.symbol} - ${quote.name}**\n\n💰 **Current Price**: $${quote.price?.toFixed(2) || 'N/A'}\n${changeColor} **Change**: ${changeText}\n📊 **Day Range**: $${quote.dayLow?.toFixed(2) || 'N/A'} - $${quote.dayHigh?.toFixed(2) || 'N/A'}\n📈 **Volume**: ${quote.volume?.toLocaleString() || 'N/A'}\n🏢 **Market Cap**: $${quote.marketCap?.toLocaleString() || 'N/A'}\n\n_Market State: ${quote.marketState}_`;
        } else {
          return `Sorry, I couldn't find data for ${symbol}. Please check the symbol and try again. Popular symbols include AAPL, TSLA, MSFT, GOOGL, AMZN, etc.`;
        }
      }
      return "I'd be happy to get stock information for you! Please specify a stock symbol like AAPL, TSLA, or MSFT. For example: \"What's the price of AAPL?\" or \"Get quote for Tesla\"";
    }
    
    // Trending stocks - enhanced detection
    if (lowerQuery.includes('trending') || lowerQuery.includes('popular') || lowerQuery.includes('hot') || 
        lowerQuery.includes('what\'s trending') || lowerQuery.includes('top stocks')) {
      const response = await mcpClient.getTrendingStocks();
      if (response.success && response.data.symbols.length > 0) {
        const symbols = response.data.symbols.slice(0, 10);
        const symbolList = symbols.map((symbol, index) => `${index + 1}. ${symbol}`).join('\n');
        return `🔥 **Top Trending Stocks (${response.data.region})**\n\n${symbolList}\n\n_Data updated: ${new Date(response.data.jobTimestamp).toLocaleString()}_\n\n💡 Want to know more about any of these stocks? Just ask me for a quote!`;
      }
    }
    
    // Market indices - enhanced detection
    if (lowerQuery.includes('indices') || lowerQuery.includes('market') || lowerQuery.includes('s&p') || 
        lowerQuery.includes('dow') || lowerQuery.includes('nasdaq') || lowerQuery.includes('how are markets') ||
        lowerQuery.includes('market performance') || lowerQuery.includes('overall market')) {
      const response = await mcpClient.getMarketIndices();
      if (response.success && response.data.length > 0) {
        let result = '📊 **Major Market Indices**\n\n';
        response.data.forEach(index => {
          const change = index.change && index.changePercent ? 
            `${index.change > 0 ? '📈' : '📉'} ${index.change > 0 ? '+' : ''}${index.change.toFixed(2)} (${index.change > 0 ? '+' : ''}${index.changePercent.toFixed(2)}%)` : 
            'N/A';
          result += `**${index.symbol}** - $${index.price?.toFixed(2) || 'N/A'} ${change}\n`;
        });
        result += '\n_Real-time market data_';
        return result;
      }
    }
    
    // Financial news - enhanced detection
    if (lowerQuery.includes('news') || lowerQuery.includes('headlines') || lowerQuery.includes('what\'s happening')) {
      const symbolMatch = query.match(/\b([A-Z]{1,5})\b/);
      const symbol = symbolMatch ? symbolMatch[1] : undefined;
      const response = await mcpClient.getFinancialNews(symbol, 5);
      if (response.success && response.data.news.length > 0) {
        let result = `📰 **Latest Financial News${symbol ? ` for ${symbol}` : ''}**\n\n`;
        response.data.news.slice(0, 3).forEach((news, index) => {
          const publishDate = new Date(news.providerPublishTime).toLocaleDateString();
          result += `${index + 1}. **${news.title}**\n   📰 _${news.publisher}_ • ${publishDate}\n\n`;
        });
        result += `💡 Want to read more? Check the full articles on Yahoo Finance!`;
        return result;
      }
    }
    
    // Currency rates - enhanced detection
    if (lowerQuery.includes('currency') || lowerQuery.includes('exchange') || lowerQuery.includes('rate') ||
        lowerQuery.includes('convert') || lowerQuery.includes('eur') || lowerQuery.includes('gbp') || lowerQuery.includes('jpy')) {
      const currencyMatch = query.match(/([A-Z]{3})\s*(?:to|\/)\s*([A-Z]{3})/i);
      if (currencyMatch) {
        const fromCurrency = currencyMatch[1].toUpperCase();
        const toCurrency = currencyMatch[2].toUpperCase();
        const response = await mcpClient.getCurrencyRates(fromCurrency, toCurrency);
        if (response.success && response.data.rate) {
          const changeText = response.data.change && response.data.changePercent ? 
            `${response.data.change > 0 ? '📈' : '📉'} ${response.data.change > 0 ? '+' : ''}${response.data.change.toFixed(4)} (${response.data.change > 0 ? '+' : ''}${response.data.changePercent.toFixed(2)}%)` : 
            'N/A';
          
          return `💱 **${fromCurrency} to ${toCurrency} Exchange Rate**\n\n💰 **Current Rate**: ${response.data.rate.toFixed(4)}\n${changeText}\n📊 **Day Range**: ${response.data.dayLow?.toFixed(4) || 'N/A'} - ${response.data.dayHigh?.toFixed(4) || 'N/A'}\n\n_Last updated: ${response.data.lastUpdate}_`;
        } else {
          return `Sorry, I couldn't get the exchange rate for ${fromCurrency}/${toCurrency}. Please check the currency codes and try again.`;
        }
      }
      return "I can help you with currency exchange rates! Please specify the currencies like \"USD to EUR rate\" or \"Exchange rate GBP/USD\". Common currencies include USD, EUR, GBP, JPY, CAD, AUD.";
    }
    
    // General conversation responses
    if (lowerQuery.includes('thank') || lowerQuery.includes('thanks')) {
      return "You're welcome! 😊 I'm here to help you with all your financial queries. Feel free to ask me about stocks, markets, news, or currency rates anytime!";
    }
    
    if (lowerQuery.includes('goodbye') || lowerQuery.includes('bye') || lowerQuery.includes('see you')) {
      return "👋 Goodbye! Thanks for using the financial assistant. Have a great day and happy trading! 📈";
    }
    
    // Default conversational response
    return "I'm your financial assistant! 🤖 While I specialize in real-time market data, I'd love to help you with:\n\n📈 **Stock Information** - \"What's the price of AAPL?\"\n🔥 **Market Trends** - \"What stocks are trending?\"\n📊 **Market Overview** - \"How are the markets?\"\n📰 **Financial News** - \"Get latest market news\"\n💱 **Currency Rates** - \"USD to EUR rate\"\n\nWhat financial information can I help you with today?";
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: '1',
        content: '👋 Hello! I\'m your AI financial assistant powered by real-time market data. I can help you with stocks, market trends, financial news, and currency rates. What would you like to know about today\'s markets?',
        isUser: false,
        timestamp: new Date()
      }
    ]);
    setError(null);
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-title">
          <h3>💬 Financial Chat Assistant</h3>
          {chatGPTAvailable && (
            <span className="chatgpt-badge">🤖 ChatGPT Enhanced</span>
          )}
        </div>
        <button onClick={clearChat} className="clear-chat-btn">
          🗑️ Clear
        </button>
      </div>

      <div className="chat-messages">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`chat-message ${message.isUser ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-content">
              {message.content}
              {message.error && (
                <div className="message-error">
                  <small>Note: {message.error}</small>
                </div>
              )}
            </div>
            <div className="message-time">
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="chat-message assistant-message">
            <div className="message-content">
              <div className="spinner"></div>
              <span style={{ marginLeft: '10px' }}>Thinking...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="chat-error">
          <ErrorMessage error={error} onRetry={() => setError(null)} />
        </div>
      )}

      <div className="chat-input">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about stocks, market data, or financial news..."
          disabled={isLoading}
          rows={2}
        />
        <button
          onClick={handleSendMessage}
          disabled={!inputMessage.trim() || isLoading}
          className="send-button"
        >
          {isLoading ? '⏳' : '📤'}
        </button>
      </div>

      <div className="chat-suggestions">
        <p><strong>Try asking:</strong></p>
        <div className="suggestion-chips">
          <button
            onClick={() => setInputMessage('Get quote for AAPL')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            📈 AAPL quote
          </button>
          <button
            onClick={() => setInputMessage('Price of TSLA')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            📊 TSLA price
          </button>
          <button
            onClick={() => setInputMessage('Show trending stocks')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            🔥 Trending stocks
          </button>
          <button
            onClick={() => setInputMessage('Get market indices')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            📊 Market indices
          </button>
          <button
            onClick={() => setInputMessage('Get financial news')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            📰 Financial news
          </button>
          <button
            onClick={() => setInputMessage('USD to EUR rate')}
            disabled={isLoading}
            className="suggestion-chip"
          >
            💱 USD/EUR rate
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
